/**
 * Finance → Investors: investments, entities, distributions and payments.
 *
 * Any write can change what an investor is owed, what the company earned, or
 * who a vehicle's weeks belong to, so every write refreshes investments, the
 * vehicle screens and Finance together.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  DividendInput,
  DividendResult,
  InvestmentCorrectionInput,
  InvestmentCorrectionResult,
  InvestmentCreateInput,
  InvestmentDetail,
  InvestmentEntityInput,
  InvestmentEntityView,
  InvestmentList,
  InvestmentListQuery,
  InvestmentPaymentInput,
  InvestmentPaymentView,
  InvestmentPermission,
  InvestmentUpdateInput,
  InvestorDetail,
  InvestorInput,
  InvestorView,
  MemberRole,
  ProfitShareInput,
  ProfitShareResult,
  VehicleTermsChangeInput,
  VehicleTermsChangeResult,
} from '@cenox/shared';
import { api, qs } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';

export function useInvalidateInvestments(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all(
      ['investments', 'riderFinance', 'riderDirectory', 'finance', 'accountability', 'dashboard', 'dashboard-growth'].map((k) =>
        client.invalidateQueries({ queryKey: [k] }),
      ),
    );
  };
}

export function useInvestments(query: InvestmentListQuery = {}): UseQueryResult<InvestmentList> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['investments', 'list', path(''), query],
    queryFn: async () =>
      (await api.get<{ data: InvestmentList }>(path(`/investments${qs(query as Record<string, string | undefined>)}`))).data,
    staleTime: 15_000,
  });
}

export function useInvestment(id: string | null): UseQueryResult<InvestmentDetail> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['investments', 'one', path(''), id],
    queryFn: async () => (await api.get<{ data: InvestmentDetail }>(path(`/investments/${id!}`))).data,
    enabled: id !== null,
    staleTime: 10_000,
  });
}

export function useInvestorProfiles(): UseQueryResult<InvestorView[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['investments', 'investors', path('')],
    queryFn: async () => (await api.get<{ data: InvestorView[] }>(path('/investments/investors'))).data,
    staleTime: 30_000,
  });
}

export function useInvestorProfile(id: string | null): UseQueryResult<InvestorDetail> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['investments', 'investor', path(''), id],
    queryFn: async () => (await api.get<{ data: InvestorDetail }>(path(`/investments/investors/${id!}`))).data,
    enabled: id !== null,
    staleTime: 15_000,
  });
}

export function useSaveInvestorProfile(): UseMutationResult<
  { data: InvestorDetail },
  Error,
  { id: string | null; input: Partial<InvestorInput> }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestments();
  return useMutation({
    mutationFn: ({ id, input }) =>
      id
        ? api.patch<{ data: InvestorDetail }>(path(`/investments/investors/${id}`), input)
        : api.post<{ data: InvestorDetail }>(path('/investments/investors'), input),
    onSuccess: invalidate,
  });
}

export function useSaveInvestment(): UseMutationResult<
  { data: InvestmentDetail },
  Error,
  { id: string | null; input: InvestmentCreateInput | InvestmentUpdateInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestments();
  return useMutation({
    mutationFn: ({ id, input }) =>
      id
        ? api.patch<{ data: InvestmentDetail }>(path(`/investments/${id}`), input)
        : api.post<{ data: InvestmentDetail }>(path('/investments'), input),
    onSuccess: invalidate,
  });
}

export function useDeleteInvestment(): UseMutationResult<unknown, Error, string> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestments();
  return useMutation({
    mutationFn: (id) => api.del(path(`/investments/${id}`)),
    onSuccess: invalidate,
  });
}

export function useUploadAgreement(): UseMutationResult<unknown, Error, { id: string; file: File }> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestments();
  return useMutation({
    mutationFn: ({ id, file }) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload(path(`/investments/${id}/agreement`), form);
    },
    onSuccess: invalidate,
  });
}

export function useEntities(enabled = true): UseQueryResult<InvestmentEntityView[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['investments', 'entities', path('')],
    queryFn: async () => (await api.get<{ data: InvestmentEntityView[] }>(path('/investments/entities'))).data,
    enabled,
    staleTime: 30_000,
  });
}

export function useSaveEntity(): UseMutationResult<
  { data: InvestmentEntityView },
  Error,
  { id: string | null; input: InvestmentEntityInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestments();
  return useMutation({
    mutationFn: ({ id, input }) =>
      id
        ? api.patch<{ data: InvestmentEntityView }>(path(`/investments/entities/${id}`), input)
        : api.post<{ data: InvestmentEntityView }>(path('/investments/entities'), input),
    onSuccess: invalidate,
  });
}

/** Preview or post: the same body, the same answer, and only `post` saves. */
function usePreviewThenPost<I, R>(
  previewPath: (input: I) => string,
  postPath: (input: I) => string,
  body: (input: I) => unknown,
): { preview: UseMutationResult<{ data: R }, Error, I>; post: UseMutationResult<{ data: R }, Error, I> } {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestments();
  return {
    preview: useMutation({ mutationFn: (input: I) => api.post<{ data: R }>(path(previewPath(input)), body(input)) }),
    post: useMutation({ mutationFn: (input: I) => api.post<{ data: R }>(path(postPath(input)), body(input)), onSuccess: invalidate }),
  };
}

export function useDividend() {
  return usePreviewThenPost<DividendInput, DividendResult>(
    () => '/investments/dividends/preview',
    () => '/investments/dividends',
    (i) => i,
  );
}

export function useProfitShare() {
  return usePreviewThenPost<{ id: string; input: ProfitShareInput }, ProfitShareResult>(
    ({ id }) => `/investments/${id}/profit-shares/preview`,
    ({ id }) => `/investments/${id}/profit-shares`,
    ({ input }) => input,
  );
}

export function useNextProfitPeriod(id: string | null): UseQueryResult<{ periodStart: string | null; periodEnd: string | null; blocked: string | null }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['investments', 'next', path(''), id],
    queryFn: async () => (await api.get<{ data: { periodStart: string | null; periodEnd: string | null; blocked: string | null } }>(path(`/investments/${id!}/profit-shares/next`))).data,
    enabled: id !== null,
  });
}

export function useInvestmentCorrection() {
  return usePreviewThenPost<{ id: string; input: InvestmentCorrectionInput }, InvestmentCorrectionResult>(
    ({ id }) => `/investments/${id}/correction/preview`,
    ({ id }) => `/investments/${id}/correction`,
    ({ input }) => input,
  );
}

export function useVehicleTerms() {
  return usePreviewThenPost<{ id: string; input: VehicleTermsChangeInput }, VehicleTermsChangeResult>(
    ({ id }) => `/investments/${id}/vehicle-terms/preview`,
    ({ id }) => `/investments/${id}/vehicle-terms`,
    ({ input }) => input,
  );
}

export function useRecordInvestmentPayment(): UseMutationResult<
  { data: InvestmentPaymentView; replayed: boolean },
  Error,
  { id: string; input: InvestmentPaymentInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestments();
  return useMutation({
    mutationFn: ({ id, input }) => api.post<{ data: InvestmentPaymentView; replayed: boolean }>(path(`/investments/${id}/payments`), input),
    onSuccess: invalidate,
  });
}

export function useVoidInvestmentMoney(): UseMutationResult<unknown, Error, { kind: 'payment' | 'distribution'; id: string; reason: string }> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestments();
  return useMutation({
    mutationFn: ({ kind, id, reason }) =>
      api.post(path(kind === 'payment' ? `/investments/payments/${id}/void` : `/investments/distributions/${id}/void`), { reason }),
    onSuccess: invalidate,
  });
}

export interface InvestmentAccessRow {
  memberId: string;
  name: string;
  email: string | null;
  role: MemberRole;
  permissions: InvestmentPermission[];
  editable: boolean;
}

export function useInvestmentAccess(enabled: boolean): UseQueryResult<InvestmentAccessRow[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['investments', 'access', path('')],
    queryFn: async () => (await api.get<{ data: InvestmentAccessRow[] }>(path('/investments/access'))).data,
    enabled,
  });
}

export function useSetInvestmentAccess(): UseMutationResult<unknown, Error, { memberId: string; permissions: InvestmentPermission[] }> {
  const path = useBusinessPath();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, permissions }) => api.put(path(`/investments/access/${memberId}`), { permissions }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['investments', 'access'] }),
  });
}
